import { useState, useEffect, useRef, CSSProperties } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, MessageSquareWarning, CreditCard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { withLang, stripLangPrefix } from '../utils/routes';

// Static nav structure — paths resolved dynamically via withLang inside the component
const NAV_LEFT  = [
  { base: '/ai-consulting', sv: 'Tjänster',    en: 'Services' },
  { base: '/automations',   sv: 'Automationer', en: 'Automations' },
];
const NAV_RIGHT = [
  { base: '/exemple', sv: 'Exempel',  en: 'Examples' },
  { base: '/contact',   sv: 'Kontakt',  en: 'Contact' },
];
const PRODUCT_BASES = [
  { base: '/klagobot',   label: 'Klagobot',   icon: MessageSquareWarning, descSv: 'Skriv klagomålsbrev med AI',         descEn: 'Write complaint letters with AI' },
  { base: '/swishkassa', label: 'Swishkassa',  icon: CreditCard,           descSv: 'Betalningslösning för föreningar',   descEn: 'Payment solution for associations' },
];

// Artists route kept alive but hidden from all navigation
const mobileExtraLinks: { base: string; label: string }[] = [];

const CorporateNavbar = () => {
  const [scrolled, setScrolled]         = useState(false);
  const [menuOpen, setMenuOpen]         = useState(false);
  const [produkterOpen, setProduktOpen] = useState(false);
  const produkterRef                    = useRef<HTMLDivElement>(null);
  const location                        = useLocation();
  const navigate                        = useNavigate();
  const { currentLang, setLang }        = useLanguage();

  const isEn = currentLang === 'en';
  const wl   = (base: string) => withLang(base, currentLang);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); setProduktOpen(false); }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (produkterRef.current && !produkterRef.current.contains(e.target as Node)) {
        setProduktOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActive = (base: string) => location.pathname === wl(base);

  /**
   * Toggle language: strip current lang prefix, apply new lang, navigate.
   * Works for any route — no explicit map needed.
   */
  const toggleLanguage = () => {
    const newLang  = isEn ? 'sv' : 'en';
    setLang(newLang);
    const basePath = stripLangPrefix(location.pathname);
    navigate(withLang(basePath, newLang), { replace: false });
  };

  const headerStyle: CSSProperties = scrolled
    ? {
        background: 'rgba(10,1,24,0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
      }
    : {
        background: 'transparent',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        borderBottom: 'none',
        boxShadow: 'none',
      };

  const ctaStyle: CSSProperties = {
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 0 16px rgba(99,102,241,0.35)',
    transition: 'all 0.2s ease',
  };

  const navLinkStyle = (active: boolean): CSSProperties => ({
    fontSize: 14,
    padding: '6px 14px',
    borderRadius: 8,
    position: 'relative',
    color: active ? '#a5b4fc' : 'rgba(255,255,255,0.82)',
    fontWeight: active ? 600 : 400,
    background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
    textDecoration: 'none',
    transition: 'color 0.2s ease, background 0.2s ease',
  });

  const renderNavLink = (base: string, sv: string, en: string) => {
    const label  = isEn ? en : sv;
    const path   = wl(base);
    const active = isActive(base);
    return (
      <Link
        key={base}
        to={path}
        style={navLinkStyle(active)}
        onMouseEnter={e => {
          if (!active) {
            (e.currentTarget as HTMLElement).style.color = '#ffffff';
            (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)';
          }
        }}
        onMouseLeave={e => {
          if (!active) {
            (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.82)';
            (e.currentTarget as HTMLElement).style.background = 'transparent';
          }
        }}
      >
        {label}
        {active && (
          <span style={{
            position: 'absolute',
            bottom: -2,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '60%',
            height: 2,
            borderRadius: 2,
            background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
          }} />
        )}
      </Link>
    );
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        transition: 'all 0.3s ease',
        ...headerStyle,
      }}
    >
      <div className="container mx-auto px-6 flex items-center justify-between h-16">

        {/* Logo — link respects current language */}
        <Link to={wl('/')} className="flex items-center group">
          <img
            src="/aifinity_logo_banner.png"
            alt="Aifinity"
            style={{
              height: 32,
              width: 'auto',
              objectFit: 'contain',
              filter: 'brightness(0) invert(1)',
              transition: 'opacity 0.2s ease',
            }}
          />
        </Link>

        {/* Desktop nav — order: Tjänster, Automationer, Produkter, Exempel, Kontakt */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV_LEFT.map(({ base, sv, en }) => renderNavLink(base, sv, en))}

          {/* Produkter dropdown */}
          <div ref={produkterRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setProduktOpen(v => !v)}
              style={{
                fontSize: 14,
                padding: '6px 14px',
                borderRadius: 8,
                color: produkterOpen ? '#a5b4fc' : 'rgba(255,255,255,0.82)',
                fontWeight: 400,
                background: produkterOpen ? 'rgba(255,255,255,0.08)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                transition: 'color 0.2s ease, background 0.2s ease',
              }}
            >
              {isEn ? 'Products' : 'Produkter'}
              <ChevronDown size={13} style={{ transition: 'transform 0.2s', transform: produkterOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </button>

            <AnimatePresence>
              {produkterOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 6, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 4, scale: 0.97 }}
                  transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: 8,
                    width: 240,
                    background: 'rgba(10,1,24,0.97)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 14,
                    padding: '8px',
                    boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
                    zIndex: 100,
                  }}
                >
                  {PRODUCT_BASES.map(({ base, label, icon: Icon, descSv, descEn }) => (
                    <Link
                      key={base}
                      to={wl(base)}
                      style={{ textDecoration: 'none', display: 'block' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '10px 12px',
                          borderRadius: 10,
                          cursor: 'pointer',
                          transition: 'background 0.15s ease',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                      >
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon size={14} color="white" />
                        </div>
                        <div>
                          <p style={{ color: 'white', fontSize: 13, fontWeight: 600, marginBottom: 1 }}>{label}</p>
                          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>{isEn ? descEn : descSv}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {NAV_RIGHT.map(({ base, sv, en }) => renderNavLink(base, sv, en))}
        </nav>

        {/* Language toggle + CTA */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={toggleLanguage}
            style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.45)',
              fontWeight: 600,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              letterSpacing: '0.04em',
              transition: 'color 0.2s ease',
              padding: '4px 6px',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.8)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.45)'; }}
            aria-label={isEn ? 'Byt till svenska' : 'Switch to English'}
          >
            {isEn ? 'SV' : 'EN'}
          </button>
          <Link to={wl('/contact')}>
            <button
              style={ctaStyle}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 0 24px rgba(99,102,241,0.55)';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 0 16px rgba(99,102,241,0.35)';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
              }}
            >
              {isEn ? 'Contact us →' : 'Kontakta oss →'}
            </button>
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          style={{ color: 'rgba(255,255,255,0.85)', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
          className="md:hidden"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{
              background: 'rgba(10,1,24,0.97)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderTop: '1px solid rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}
            className="md:hidden"
          >
            <nav className="container mx-auto px-6 py-4 flex flex-col gap-1">
              {[...NAV_LEFT, ...NAV_RIGHT].map(({ base, sv, en }) => (
                <Link
                  key={base}
                  to={wl(base)}
                  style={{
                    fontSize: 14,
                    color: 'rgba(255,255,255,0.7)',
                    padding: '12px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    transition: 'color 0.2s ease',
                    textDecoration: 'none',
                    display: 'block',
                  }}
                  onClick={() => setMenuOpen(false)}
                >
                  {isEn ? en : sv}
                </Link>
              ))}
              {PRODUCT_BASES.map(({ base, label }) => (
                <Link
                  key={base}
                  to={wl(base)}
                  style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', textDecoration: 'none', display: 'block' }}
                  onClick={() => setMenuOpen(false)}
                >
                  {label}
                </Link>
              ))}
              <button
                onClick={() => { toggleLanguage(); setMenuOpen(false); }}
                style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 600, marginTop: 4, alignSelf: 'flex-start' }}
              >
                {isEn ? 'Svenska' : 'English'}
              </button>
              <Link to={wl('/contact')} style={{ marginTop: 12 }} onClick={() => setMenuOpen(false)}>
                <button style={{ ...ctaStyle, width: '100%', padding: '12px 20px', borderRadius: 10 }}>
                  {isEn ? 'Contact us →' : 'Kontakta oss →'}
                </button>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default CorporateNavbar;
